import React from 'react';

import get from 'lodash/get';

import getExampleSearchResults from './getExampleSearchResults';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {
  withSystem,
  Plugins,
  withMeta,
  Atom,
  AtomRenderProps,
} from '@packages/systems/wfdl';
import {
  iconName,
  pluginName,
  qualify,
  typeName,
} from '@packages/systems/wfdl-Qualified';

import {
  HIGHLIGHTING_ENABLED_DEFAULT,
  RESULTS_LENGTH_DEFAULT,
  SNIPPET_LENGTH_DEFAULT,
} from '@packages/systems/core/constants/SiteSearch';

import {
  SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS,
  SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
} from '@packages/systems/core/constants/SharedConstraintRules';

import {
  TText,
  TListOfElement,
  TRecord,
  TEnum,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment} from '@packages/systems/render';
import {
  EvaluatedNodeData,
  findChild,
  getBooleanOrElse,
  getInPojo,
  getNumberOrElse,
  getStringOrElse,
  getTag,
} from '@packages/utilities/data-utils';
import {
  isSearchResultEmptyMessage,
  isSearchResultList,
} from '@packages/utilities/elementTypeUtils';

const getSearchWrapperUiState = (d: EvaluatedNodeData) =>
  getInPojo(d, ['temp', 'state']);
const getSearchPreviewQuery = (d: EvaluatedNodeData) =>
  getStringOrElse(getInPojo(d, ['temp', 'searchPreviewQuery']), '');
const getResultsLength = (d: EvaluatedNodeData) =>
  getNumberOrElse(
    getInPojo(d, ['search', 'resultsLength']),
    RESULTS_LENGTH_DEFAULT
  );
const getSnippetLength = (d: EvaluatedNodeData) =>
  getNumberOrElse(
    getInPojo(d, ['search', 'snippetLength']),
    SNIPPET_LENGTH_DEFAULT
  );
const getHighlightingEnabled = (d: EvaluatedNodeData) =>
  getBooleanOrElse(
    getInPojo(d, ['search', 'highlightingEnabled']),
    HIGHLIGHTING_ENABLED_DEFAULT
  );

const SEARCH_RESULT_COUNT_PATH = ['site', 'search', 'count'];
// @ts-expect-error - TS7006 - Parameter 'data' implicitly has an 'any' type.
const getSearchResultCount = (data) => get(data, SEARCH_RESULT_COUNT_PATH, 0);
const SEARCH_RESULTS_EDGES_PATH = ['site', 'search', 'edges'];
// @ts-expect-error - TS7006 - Parameter 'data' implicitly has an 'any' type.
const getSearchResultEdges = (data) => get(data, SEARCH_RESULTS_EDGES_PATH, []);

const SearchResultWrapper = Atom.createRender(
  ({
    attributes,
    classes,
    generateAttributes,
    renderChild,
    context,
    data: elementData,
  }: AtomRenderProps) => {
    const runtimeEnvironment = useRuntimeEnvironment();

    if (runtimeEnvironment === WebflowEnvironments.EXPORT) {
      return null;
    }

    // The tag field is always present via the field's schema default.
    const Tag = getTag(getInPojo(elementData, ['tag'])) as React.ElementType;
    let children = null;

    const resultsLength = getResultsLength(elementData);
    const snippetLength = getSnippetLength(elementData);
    const isDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;

    const query = isDesigner
      ? getSearchPreviewQuery(elementData).trim()
      : context.get('query');

    let data = context.get('searchResultsData');

    if (!data || !data.site || data.site.search === null) {
      // If data.site.search is null, the site hasn't been indexed yet.
      // In this case, we want to show the example results.
      data = getExampleSearchResults(
        null, // Don't query example results in SSR
        resultsLength,
        snippetLength
      );
    }

    if (isDesigner && !context.get('isIndexed')) {
      data = getExampleSearchResults(query, resultsLength, snippetLength);
    }

    if (
      getSearchResultCount(data) === 0 ||
      getSearchWrapperUiState(elementData) === 'empty'
    ) {
      // Empty state
      const searchResultEmpty = findChild(
        elementData,
        isSearchResultEmptyMessage
      );
      children = renderChild(searchResultEmpty);
    } else {
      // Items state
      const edges = getSearchResultEdges(data);
      const highlightingEnabled = getHighlightingEnabled(elementData);

      const searchResultList = findChild(elementData, isSearchResultList);
      children = renderChild(
        searchResultList,
        context
          .set('query', query)
          .set('edges', edges)
          .set('highlightingEnabled', highlightingEnabled)
      );
    }
    return <Tag {...generateAttributes(attributes, classes)}>{children}</Tag>;
  }
);

const config = createIntegratedAtom({
  displayName: 'Search Result Wrapper',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Search'), iconName('SearchResults')),
  },
  uiStateOnSelected: 'items',
  showEmpty: true,
  render: SearchResultWrapper,
  constraints: {
    descendants: SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
    ancestors: SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS,
  },
  fields: {
    children: TListOfElement,
    tag: withSystem(
      {
        label: 'Tag',
        private: true,
        defaultValue: 'div',
      },
      TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
    ),
    temp: TRecord({
      searchPreviewQuery: withMeta(
        {
          placeholder: 'Search...',
        },
        withSystem(
          {
            defaultValue: '',
            label: 'Preview results',
            isFullWidth: true,
            isLocalizable: true,
          },
          TText
        )
      ),
      note: withSystem(
        {
          labelVariant: 'hidden',
        },
        TTypeConstructor(qualify(pluginName('Search'), typeName('Note')))
      ),
      state: withSystem(
        {
          labelVariant: 'hidden',
          defaultValue: 'items',
          isLocalizable: true,
        },
        TEnum({default: 'Default', empty: 'No Results'})
      ),
    }),
    search: TRecord({
      type: withSystem(
        {
          defaultValue: 'wrapper',
          private: true,
        },
        TText
      ),
      resultsLength: withSystem(
        {
          label: 'Results length',
          defaultValue: RESULTS_LENGTH_DEFAULT,
          isLocalizable: true,
        },
        TTypeConstructor(
          qualify(Plugins.name('Basic'), typeName('SliderSearchResultsLength'))
        )
      ),
      snippetLength: withSystem(
        {
          label: 'Snippet length',
          defaultValue: SNIPPET_LENGTH_DEFAULT,
          isLocalizable: true,
        },
        TTypeConstructor(
          qualify(
            Plugins.name('Basic'),
            typeName('SliderSearchResultsSnippets')
          )
        )
      ),
      highlightingEnabled: withSystem(
        {
          label: 'Highlight search terms in snippets',
          labelVariant: 'hidden',
          defaultValue: HIGHLIGHTING_ENABLED_DEFAULT,
          isLocalizable: true,
        },
        TTypeConstructor(
          qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
        )
      ),
    }),
  },
});

export default config;
